import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { usePageContext } from 'vike-react/usePageContext';
import { Link } from '../Link';
import { PRIMARY_NAV_LINKS, PRIMARY_NAV_ARIA_LABEL, primaryNavLabel } from './primary-nav-links';
import { MyJobsNavEntry } from './MyJobsNavEntry';
import { Logo } from './Logo';
import { LanguageSelector } from './LanguageSelector';
import { UserMenu } from './UserMenu';
import type { IntlShape } from 'react-intl';
import { useJobIslandContext } from '../../contexts/JobIslandContext';
import { useAuthOptional } from '../../contexts/AuthContext';
import { getLocaleFromPathname, normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import { trackEvent } from '../../utils/analytics/analytics';
import { CORE_HEADER_STICKY, CORE_NAV_LINK, CORE_LOGO_TEXT } from '../../constants/corePublicTheme';
import { Msg, useFormatMessage } from '../../i18n-review/client/Msg';
import { useNavbarLiveIndexEnabled } from '../../utils/feature-flags/navbar-live-index-flag';
import { SearchLauncher } from './nav-live-index/SearchLauncher';
import { AccountPill } from './nav-live-index/AccountPill';
import { BarsMenuButton } from './nav-live-index/BarsMenuButton';
import { IndexStrip } from './nav-live-index/IndexStrip';
import { MobileIndexSubtitle } from './nav-live-index/MobileIndexSubtitle';
import type { MenuPulse } from '../../server/routes/menu-pulse';
import {
  LOGO_COMPACT_IMG_CLASS,
  LOGO_COMPACT_ROW,
  LOGO_COMPACT_TEXT_CLASS,
  LOGO_COMPACT_WRAP,
  MOBILE_SUBTITLE_WRAP,
  NAV_PART,
} from './nav-live-index/nav-styles';

export interface NavigationNavbarProps {
  intl: IntlShape;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  clearSearch: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
  user?: {
    image?: string;
    picture?: string;
    name?: string;
  } | null;
}

/**
 * Navigation navbar component - shows on non-SRP pages
 * Contains logo, navigation links, tools menu, language selector, and user menu
 */
export const NavigationNavbar: React.FC<NavigationNavbarProps> = (props) => {
  // Phase 1 review fix 2.8: seeded from the cookie server-side (server/vike-handler.ts ->
  // pageContext.navbarLiveIndexEnabled) so an opted-in visitor's first client render already
  // shows the new header, then re-confirmed against the cookie post-hydrate (see useFeatureFlag)
  // so there's still no SSR/client markup mismatch for anyone. Flag off renders
  // NavigationNavbarClassic, byte-for-byte what this component rendered before the navbar
  // live-index redesign (TEC navbar redesign Phase 1).
  const pageContext = usePageContext();
  const ssrLiveIndexEnabled = (pageContext as { navbarLiveIndexEnabled?: boolean } | undefined)?.navbarLiveIndexEnabled;
  const liveIndexEnabled = useNavbarLiveIndexEnabled(ssrLiveIndexEnabled);
  return liveIndexEnabled ? <NavigationNavbarLiveIndex {...props} /> : <NavigationNavbarClassic {...props} />;
};

/** Flag-off path: unchanged since before the navbar live-index redesign. */
const NavigationNavbarClassic: React.FC<NavigationNavbarProps> = ({
  intl,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  clearSearch,
  user,
}) => {
  const formatMessage = useFormatMessage();
  const pageContext = usePageContext();
  const jobIsland = useJobIslandContext();
  const pathname = jobIsland?.pathname ?? (pageContext as any)?.urlPathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  // Prefer job island, then pageContext, then intl, then pathname so static and hydrated both show correct locale
  const rawLocale = jobIsland?.locale ?? pageContext?.locale ?? intl?.locale ?? getLocaleFromPathname(pathname) ?? 'en';
  const locale = normalizeSupportedLocale(rawLocale);

  const auth = useAuthOptional();
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  const showGuestSignIn = hasMounted && auth && !auth.isAuthenticated && typeof auth.signIn === 'function';

  return (
    <header className={CORE_HEADER_STICKY} role="banner">
      <nav className="flex items-center gap-2 py-3 px-4 xl:px-16 md:px-10" aria-label={PRIMARY_NAV_ARIA_LABEL[locale] ?? PRIMARY_NAV_ARIA_LABEL.en}>
        {/* Left side: Logo */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Logo clearSearch={clearSearch} variant="horizontal" locale={locale} />
          <Logo clearSearch={clearSearch} variant="compact" locale={locale} />
        </div>

        {/* Center: same primary nav on every page — link set comes from PRIMARY_NAV_LINKS,
            shared with the static ResourceNavbar twin so the two navbars cannot drift. */}
        <nav className="hidden lg:flex flex-1 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {/* mx-auto (not justify-center on the scroll container) so links stay centered when
              they fit, but when a long locale's labels overflow, scrolling from the start
              reaches every link instead of clipping the first one off unreachably. */}
          <div className="flex items-center gap-2 mx-auto">
            {PRIMARY_NAV_LINKS.map((link) => (
              <Link
                key={link.key}
                href={link.logicalPath}
                locale={locale}
                className={CORE_NAV_LINK}
              >
                {primaryNavLabel(link, locale)}
              </Link>
            ))}
          </div>
        </nav>

        {/* Right side: Language Selector (xl screens and above) and Hamburger Menu */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          {showGuestSignIn ? (
            <div className="hidden md:flex items-center gap-2 mr-1">
              <button
                type="button"
                onClick={() => {
                  trackEvent({ name: 'auth_interaction', params: { action: 'sign_in_click', surface: 'navbar', locale } });
                  auth.signIn();
                }}
                className="inline-flex items-center px-4 h-[52px] rounded-full text-sm font-semibold text-jc-ink-secondary border border-jc-border bg-jc-surface/90 hover:bg-jc-elevated hover:text-jc-ink hover:scale-[1.02] active:scale-[0.97] transition-[background-color,color,transform] duration-150 whitespace-nowrap cursor-pointer"
              >
                <Msg
                  id="engagement.nav.signIn"
                  defaultMessage="Sign in"
                  description="Desktop nav sign-in button for guests"
                />
              </button>
            </div>
          ) : null}
          <div className="hidden sm:block">
            <MyJobsNavEntry locale={locale} />
          </div>
          <LanguageSelector intl={intl} formatMessage={formatMessage} locale={locale} />
          <UserMenu
            intl={intl}
            formatMessage={formatMessage}
            user={user}
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
          />
        </div>
      </nav>
    </header>
  );
};

/**
 * Flag-on path (navbar live-index redesign, TEC navbar redesign Phase 1): logo (the only home
 * link, no separate "Home" nav item), a fused search+location bar, a language track, a single
 * account pill (avatar+My Jobs, or a guest value-sheet popover), a bars-only menu button, and
 * the row-two index strip (market figures + browse-by words + freshness stamp). Mobile (<lg)
 * shows a wordmark + live subtitle line instead, and only the bars-only menu button: no search
 * bar or account pill in the top bar, matching today's mobile behaviour where search on non-SRP
 * pages lives in the hamburger menu's own search form (MobileBottomNav, which does carry a top
 * bar search field, only mounts on the SRP in this codebase).
 */
const NavigationNavbarLiveIndex: React.FC<NavigationNavbarProps> = ({ clearSearch, isMobileMenuOpen, setIsMobileMenuOpen }) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const pageContext = usePageContext();
  const jobIsland = useJobIslandContext();
  const pathname = jobIsland?.pathname ?? (pageContext as any)?.urlPathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  const rawLocale = jobIsland?.locale ?? pageContext?.locale ?? intl?.locale ?? getLocaleFromPathname(pathname) ?? 'en';
  const locale = normalizeSupportedLocale(rawLocale);
  // Phase 3 item 3.2: server-rendered figures, seeded the same way navbarLiveIndexEnabled is
  // (see server/vike-handler.ts) so the strip's first paint already has numbers.
  const initialPulse = (pageContext as { navPulse?: MenuPulse | null } | undefined)?.navPulse ?? null;

  const menuAriaLabel = formatMessage({ id: 'navigation.menu', defaultMessage: 'Menu', description: 'Button aria-label to open navigation menu' });
  const handleMenuClick = () => {
    const willOpen = !(isMobileMenuOpen ?? false);
    if (willOpen) {
      trackEvent({ name: 'nav_menu_open', params: { menu: 'user', locale } });
    }
    setIsMobileMenuOpen?.(willOpen);
  };

  return (
    <header className={CORE_HEADER_STICKY} role="banner">
      <div className="flex items-center gap-2 h-[58px] px-4 xl:px-16 md:px-10" aria-label={PRIMARY_NAV_ARIA_LABEL[locale] ?? PRIMARY_NAV_ARIA_LABEL.en} role="navigation">
        {/* Logo is the only home link: the "Home" primary nav item is intentionally absent. */}
        <div className="flex items-center gap-2 flex-shrink-0 min-w-0">
          <Logo clearSearch={clearSearch} variant="horizontal" locale={locale} />
          <Link
            href="/"
            onClick={clearSearch}
            locale={locale}
            data-jc-nav-part={NAV_PART.logoCompact}
            className={LOGO_COMPACT_WRAP}
          >
            <span className={LOGO_COMPACT_ROW}>
              <img src="/logo.svg" height="36" width="36" alt="JobCrawls home" loading="lazy" className={LOGO_COMPACT_IMG_CLASS} />
              <span className={`${LOGO_COMPACT_TEXT_CLASS} ${CORE_LOGO_TEXT}`}>JobCrawls</span>
            </span>
            <span className={MOBILE_SUBTITLE_WRAP}>
              <MobileIndexSubtitle locale={locale} formatMessage={formatMessage} initialPulse={initialPulse} />
            </span>
          </Link>
        </div>

        {/* Search + location, fused, launcher-mode off the SRP. Hidden below lg: mobile search
            on non-SRP pages lives in the hamburger menu's own search form, as it does today. */}
        <div className="hidden lg:flex flex-1 min-w-0">
          <SearchLauncher locale={locale} formatMessage={formatMessage} />
        </div>

        <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
          <div className="hidden lg:flex items-center gap-1.5">
            <LanguageSelector intl={intl} formatMessage={formatMessage} locale={locale} sizeVariant="compact40" />
            <span className="w-[10px]" aria-hidden />
            <AccountPill locale={locale} />
          </div>
          <BarsMenuButton onClick={handleMenuClick} ariaLabel={menuAriaLabel} sizeClassName="w-11 h-11 lg:w-10 lg:h-10" />
        </div>
      </div>
      <IndexStrip locale={locale} pathname={pathname} formatMessage={formatMessage} initialPulse={initialPulse} />
    </header>
  );
};
